import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { StandardEvents } from '@shopify/events';

/**
 * brenthelsnow: Size Guide dialog block (item 32). Horizon's picker morphs only the
 * <variant-picker> element on variant change, so this block re-renders itself from the
 * section HTML that fetch already returned — the selected-size column highlight follows
 * the picker without a second request (same pattern as variant-sku-table.js, Codex r1).
 * The dialog is modal, so it is always closed when a variant change can happen — the
 * morph never has to preserve an open state.
 */
class SizeChartBlock extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.closest('.shopify-section, dialog')?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.closest('.shopify-section, dialog')?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /** @param {import('@shopify/events').ProductSelectEvent} event */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;
        const fresh = detail.html.querySelector('size-chart-block');
        if (!fresh) return;
        morph(this, fresh, { childrenOnly: true });
        // Fresh server HTML carries no unit choice — the morph wipes the container
        // attribute and button states, so restore them from storage.
        for (const units of this.querySelectorAll('size-chart-units')) {
          if (units instanceof SizeChartUnits) units.apply(storedUnit());
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[size-chart] Event promise rejected:', error);
      });
  };
}

/**
 * brenthelsnow: in/cm unit toggle (B15). The snippet renders the element only when a chart
 * carries BOTH an in and a cm table; the buttons show one unit at a time by setting
 * data-unit-choice on the .size-chart-tables container (the CSS that hides the other unit
 * keys off that attribute, so no JS = stacked pairs, the fallback). The choice is shared:
 * every toggle on the page follows a click (document event), and localStorage carries it
 * across pages and into the Size Guide dialog.
 */
const UNIT_STORAGE_KEY = 'brenthelsnow:size-chart-unit';
const UNIT_CHANGE_EVENT = 'size-chart:unit-change';

/** In-memory fallback so a blocked localStorage doesn't reset the choice on a
 *  variant morph (Codex r1) — a click this pageview always beats stored state. */
let activeUnit = null;

function storedUnit() {
  if (activeUnit) return activeUnit;
  try {
    const unit = localStorage.getItem(UNIT_STORAGE_KEY);
    if (unit === 'in' || unit === 'cm') return unit;
  } catch {
    // storage blocked (private mode etc.) — fall through to the default
  }
  return 'in';
}

class SizeChartUnits extends HTMLElement {
  connectedCallback() {
    // Stable handler refs — re-adding on a reconnect is a no-op, never a double bind.
    this.addEventListener('click', this.#handleClick);
    document.addEventListener(UNIT_CHANGE_EVENT, this.#handleUnitChange);
    this.apply(storedUnit());
  }

  disconnectedCallback() {
    document.removeEventListener(UNIT_CHANGE_EVENT, this.#handleUnitChange);
  }

  /** @param {'in' | 'cm'} unit */
  apply(unit) {
    this.closest('.size-chart-tables')?.setAttribute('data-unit-choice', unit);
    // The control is a single switch (B16, burton.com-style): checked = centimetres.
    this.querySelector('[role="switch"]')?.setAttribute('aria-checked', `${unit === 'cm'}`);
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('[role="switch"]')) return;
    const current = this.closest('.size-chart-tables')?.getAttribute('data-unit-choice');
    const unit = current === 'cm' ? 'in' : 'cm';
    activeUnit = unit;
    try {
      localStorage.setItem(UNIT_STORAGE_KEY, unit);
    } catch {
      // storage blocked — the page still toggles, the choice just won't persist
    }
    document.dispatchEvent(new CustomEvent(UNIT_CHANGE_EVENT, { detail: { unit } }));
  };

  /** @param {CustomEvent<{unit: 'in' | 'cm'}>} event */
  #handleUnitChange = (event) => this.apply(event.detail.unit);
}

if (!customElements.get('size-chart-block')) {
  customElements.define('size-chart-block', SizeChartBlock);
}

if (!customElements.get('size-chart-units')) {
  customElements.define('size-chart-units', SizeChartUnits);
}
